import clipboardCopy from "clipboard-copy";
import toast from "react-hot-toast";
import { marked } from "marked";
import { toastFail, toastSuccess } from "../config";

const showCopySuccess = () => toast("Copied to clipboard", toastSuccess);
const showCopyFailure = () => toast("Could not copy to clipboard", toastFail);

export const handleCopyText = async (
  text: string | null | undefined
): Promise<boolean> => {
  if (!text) {
    showCopyFailure();
    return false;
  }

  try {
    await clipboardCopy(text);
    showCopySuccess();
    return true;
  } catch (error) {
    console.error("Failed to copy text:", error);
    showCopyFailure();
    return false;
  }
};

export const copyMarkdownAsRichText = async (
  markdown: string
): Promise<boolean> => {
  if (typeof window === "undefined") {
    return false;
  }

  const html = marked(markdown) as string;
  const tempContainer = document.createElement("div");
  tempContainer.innerHTML = html;
  const plainText = tempContainer.textContent || markdown;

  const clipboard = navigator.clipboard;
  const supportsRichClipboard =
    typeof ClipboardItem !== "undefined" &&
    clipboard !== undefined &&
    typeof clipboard.write === "function";

  if (supportsRichClipboard) {
    try {
      const richTextItem = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plainText], { type: "text/plain" }),
      });

      await clipboard.write([richTextItem]);
      showCopySuccess();
      return true;
    } catch (error) {
      console.error("Failed to copy rich text:", error);
    }
  }

  return handleCopyText(plainText);
};

export const smartTimeAgo = (sinceDate: Date, compressed = false): string => {
  const hoursInSeconds = 60 * 60;
  const daysInSeconds = 60 * 60 * 24;
  const fortnightInSeconds = 60 * 60 * 24 * 14;

  let seconds = Math.floor((Date.now() - sinceDate.getTime()) / 1000);

  if (seconds < 0) {
    seconds = 0;
  }

  if (seconds < 61) {
    return `${seconds}${compressed ? "s" : " seconds"} ago`;
  }

  if (seconds < 3601) {
    return `${Math.floor(seconds / 60)}${compressed ? "m" : " mins"} ago`;
  }

  if (seconds < daysInSeconds + 1) {
    return `${Math.floor(seconds / hoursInSeconds)}${compressed ? "h" : " hours"} ago`;
  }

  if (seconds < fortnightInSeconds + 1) {
    return `${Math.floor(seconds / daysInSeconds)}${compressed ? "d" : " days"} ago`;
  }

  return `on${sinceDate.toLocaleDateString("en-us", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  })}`;
};

export const smartTimeAgo2 = (sinceDate: Date, compressed = false): string => {
  const hoursInSeconds = 60 * 60;
  const daysInSeconds = 60 * 60 * 24;
  const fortnightInSeconds = 60 * 60 * 24 * 14;

  let seconds = Math.floor((Date.now() - sinceDate.getTime()) / 1000);

  if (seconds < 0) {
    seconds = 0;
  }

  if (seconds < 61) {
    return `${seconds}${compressed ? "s" : " seconds"} ago`;
  }

  if (seconds < 3601) {
    return `${Math.floor(seconds / 60)}${compressed ? "m" : " mins"} ago`;
  }

  if (seconds < daysInSeconds + 1) {
    return `${Math.floor(seconds / hoursInSeconds)}${compressed ? "h" : " hours"} ago`;
  }

  if (seconds < fortnightInSeconds + 1) {
    return `${Math.floor(seconds / daysInSeconds)}${compressed ? "d" : " days"} ago`;
  }

  return sinceDate.toLocaleDateString("en-us", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
};

const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const capitalLettersPattern = /[A-Z]+/g;
const numberPattern = /\d+/g;

export const validateEmail = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }

  return emailPattern.test(value);
};

export const validatePassword = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }

  const hasMinChars = value.length > 7;
  const hasCapital = capitalLettersPattern.test(value);
  const hasNumber = numberPattern.test(value);

  return hasMinChars && hasCapital && hasNumber;
};
